import {
  Box,
  Button,
  Flex,
  Input,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FileInput } from '../../../../components/FileInput'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { ApiErrorShape, toFormErrors, useAppToast } from '../../../../helpers'
import { FONT_SIZE_ADJUST, IWebsiteFont } from '../../../../interfaces/Website/Banners/IWebsiteFont'
import WebsiteFontService from '../../../../services/Website/WebsiteFontService'
import { useFontPreview } from './useFontPreview'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  /** Fonte em edição. Sem ela, o modal envia uma nova. */
  font?: IWebsiteFont
}

const EMPTY_ERRORS = { name: '', file: '', size_adjust: '' }

type IFormErrors = typeof EMPTY_ERRORS

export const FONT_PREVIEW_TEXT = 'Lorem ipsum dolor sit amet'

/** Nome sugerido a partir do arquivo: `PlayfairDisplay-Bold.woff2` vira `PlayfairDisplay Bold`. */
const nameFromFile = (file: File) =>
  file.name
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim()

/**
 * Envio e edição de uma fonte dos banners. Na edição só nome e ajuste de tamanho mudam: o arquivo
 * e o slug ficam, porque é o slug que o HTML dos banners guarda.
 */
export default function WebsiteFontFormModal({ isOpen, onClose, onSaved, font }: IProps) {
  const toast = useAppToast()
  const isEditing = !!font
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [sizeAdjust, setSizeAdjust] = useState(FONT_SIZE_ADJUST.default)
  const [errors, setErrors] = useState<IFormErrors>(EMPTY_ERRORS)
  const [isSaving, setIsSaving] = useState(false)
  const preview = useFontPreview(isOpen ? (font?.url ?? file) : null, sizeAdjust)

  useEffect(() => {
    if (!isOpen) return

    setName(font?.name ?? '')
    setFile(null)
    setSizeAdjust(font?.size_adjust ?? FONT_SIZE_ADJUST.default)
    setErrors(EMPTY_ERRORS)
  }, [isOpen, font])

  const handleSubmit = async () => {
    const nextErrors: IFormErrors = {
      ...EMPTY_ERRORS,
      name: name.trim() ? '' : 'Informe o nome da fonte.',
      file: isEditing || file ? '' : 'Escolha o arquivo da fonte.'
    }

    if (nextErrors.name || nextErrors.file) {
      setErrors(nextErrors)
      return
    }

    setIsSaving(true)

    try {
      const service = new WebsiteFontService()

      if (font) {
        await service.update(font.id, name.trim(), sizeAdjust)
        toast.success('Fonte atualizada', 'O novo tamanho já vale para os banners que usam essa fonte.')
      } else if (file) {
        await service.create(name.trim(), file, sizeAdjust)
        toast.success('Fonte enviada', 'Ela já aparece no seletor de fonte do editor dos banners.')
      }

      onSaved()
      onClose()
    } catch (error) {
      const { status, data } = (error as ApiErrorShape).response ?? {}

      if ((status === 400 || status === 422) && data?.errors) {
        setErrors(toFormErrors(EMPTY_ERRORS, data.errors))
      } else {
        toast.error(isEditing ? 'Erro ao atualizar a fonte' : 'Erro ao enviar a fonte', error)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Editar fonte' : 'Enviar fonte'}
      size="compact"
      onSubmit={handleSubmit}
      confirmAction={isEditing ? 'save' : 'create'}
      confirmLabel={isEditing ? undefined : 'Enviar'}
      isLoading={isSaving}
    >
      <Stack spacing={4}>
        {!isEditing && (
          <FileInput
            label="Arquivo"
            accept="font"
            maxSizeMB={2}
            isRequired
            value={file}
            onChange={(picked) => {
              setFile(picked)
              setErrors((prev) => ({ ...prev, file: '' }))
              if (picked && !name.trim()) setName(nameFromFile(picked))
            }}
            error={preview.isInvalid ? 'O navegador não conseguiu ler esse arquivo como fonte.' : errors.file}
            helperText="Um arquivo por estilo: para ter negrito de verdade, envie também a versão bold."
          />
        )}
        <FormField label="Nome" isRequired error={errors.name} helperText="É como a fonte aparece no editor.">
          <Input
            value={name}
            maxLength={100}
            onChange={(e) => {
              setName(e.target.value)
              setErrors((prev) => ({ ...prev, name: '' }))
            }}
          />
        </FormField>
        <FormField
          label="Ajuste de tamanho"
          error={errors.size_adjust}
          helperText="Algumas fontes, principalmente as manuscritas, são desenhadas menores. O ajuste vale para todos os banners que usam a fonte."
        >
          <Flex align="center" gap={4}>
            <Slider
              aria-label="Ajuste de tamanho"
              min={FONT_SIZE_ADJUST.min}
              max={FONT_SIZE_ADJUST.max}
              step={5}
              value={sizeAdjust}
              onChange={setSizeAdjust}
              focusThumbOnChange={false}
            >
              <SliderTrack bg="gray.100">
                <SliderFilledTrack bg="magenta.500" />
              </SliderTrack>
              <SliderThumb />
            </Slider>
            <Text w="3.5rem" textAlign="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {sizeAdjust}%
            </Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSizeAdjust(FONT_SIZE_ADJUST.default)}
              isDisabled={sizeAdjust === FONT_SIZE_ADJUST.default}
            >
              Original
            </Button>
          </Flex>
        </FormField>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Prévia no mesmo tamanho, comparada com a Montserrat
          </Text>
          {preview.family ? (
            <Text fontSize="2xl" lineHeight="short" fontFamily={`'${preview.family}', sans-serif`}>
              {FONT_PREVIEW_TEXT}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.500">
              {isEditing ? 'Carregando a fonte…' : 'Escolha o arquivo para ver a prévia.'}
            </Text>
          )}
          <Text fontSize="2xl" lineHeight="short" fontFamily="'Montserrat', sans-serif" color="gray.400">
            {FONT_PREVIEW_TEXT}
          </Text>
        </Box>
      </Stack>
    </FormModal>
  )
}
